import { Injectable, inject } from '@angular/core';
import { Subscription, take } from 'rxjs';
import {
  IndicatorService,
  ParentIndicatorResponseDto,
} from 'src/generated-sources/openapi/configuration-service/indicator';
import { BaseDataService } from './base-data.service';

export interface IndicatorDataState {
  groupedIndicators: ParentIndicatorResponseDto[];
  isLoaded: boolean;
  isLoading: boolean;
}

const initialState: IndicatorDataState = {
  groupedIndicators: [],
  isLoaded: false,
  isLoading: false,
};

@Injectable({
  providedIn: 'root',
})
export class IndicatorDataService extends BaseDataService<IndicatorDataState> {
  private readonly indicatorService = inject(IndicatorService);
  private loadSubscription?: Subscription;

  constructor() {
    super(initialState);
  }

  /**
   * Loads all grouped indicators from the catalog.
   * If a request is already in-flight, it is cancelled and a new one starts.
   * This ensures the result always reflects the current person type (sent via request header).
   */
  load(): void {
    this.loadSubscription?.unsubscribe();
    this.updateState({ isLoading: true });

    this.loadSubscription = this.indicatorService
      .listAllGrouped()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.updateState({
            groupedIndicators: response.items ?? [],
            isLoaded: true,
            isLoading: false,
          });
        },
        error: () => {
          this.updateState({ isLoading: false });
        },
      });
  }

  /**
   * Returns a Map<indicatorId, displayOrder> for all indicators in the global catalog.
   * Includes both parent indicators and their children.
   */
  getOrderMap(): Map<string, number> {
    const map = new Map<string, number>();
    let order = 0;
    const sortedParents = [...this.getStateValue().groupedIndicators].sort(
      (a, b) => a.displayOrder - b.displayOrder,
    );
    for (const parent of sortedParents) {
      map.set(parent.id, order++);
      const sortedChildren = [...(parent.indicators ?? [])].sort(
        (a, b) => a.displayOrder - b.displayOrder,
      );
      for (const child of sortedChildren) {
        map.set(child.id, order++);
      }
    }
    return map;
  }

  /**
   * Sorts items by their global displayOrder from the catalog.
   * Items whose ID is not found in the catalog are placed at the end,
   * preserving their relative order among themselves.
   *
   * @param items Items to sort
   * @param getId Function to extract the indicator ID from each item.
   *   Defaults to `item.id` when items extend `{ id: string }`.
   */
  sortByDisplayOrder<T extends { id: string }>(items: T[]): T[];
  sortByDisplayOrder<T>(items: T[], getId: (item: T) => string): T[];
  sortByDisplayOrder<T>(items: T[], getId?: (item: T) => string): T[] {
    const orderMap = this.getOrderMap();
    const getItemId = getId ?? ((item: any) => item.id as string);
    return [...items].sort((a, b) => {
      const orderA = orderMap.get(getItemId(a)) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(getItemId(b)) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }
}
